import Config from '@config';
import { normalizeInfiniteQueryData } from '@gasbi/shared/common/features/utils';
import { createApi } from '@reduxjs/toolkit/query/react';

import { CACHE_EXPIRATION_TIME_REFERENCE_BOOK } from '@/features/constants';
import { FiltersGrid, Pagination } from '@/features/types';
import { infiniteQueryOptions } from '@/features/utils';
import axiosBaseQuery from '@/utils/axiosBaseQuery';

import { AdmissionClass, AdmissionClassRequestBody } from './types';

export const admissionClassesApi = createApi({
  reducerPath: 'admissionClassesApi',
  baseQuery: axiosBaseQuery({
    baseUrl: `${Config.VITE_API_URL}/admission-classes`,
  }),
  keepUnusedDataFor: CACHE_EXPIRATION_TIME_REFERENCE_BOOK,
  tagTypes: ['AdmissionClass', 'AdmissionClasses'],
  endpoints: builder => ({
    getAdmissionClasses: builder.query<AdmissionClass[], void>({
      query: () => ({
        url: '/',
      }),
    }),
    getAdmissionClassesI: builder.infiniteQuery<
      AdmissionClass[],
      FiltersGrid,
      Pagination
    >({
      infiniteQueryOptions,
      query: ({ queryArg, pageParam }) => ({
        url: `/query`,
        params: { ...queryArg, ...pageParam },
      }),
      providesTags: () => ['AdmissionClass', 'AdmissionClasses'],
    }),
    getAdmissionClassById: builder.query<AdmissionClass, string>({
      query: id => ({
        url: `/${id}`,
      }),
      providesTags: () => ['AdmissionClass'],
    }),
    createAdmissionClass: builder.mutation<
      AdmissionClass,
      AdmissionClassRequestBody
    >({
      query: data => ({
        url: '/',
        method: 'POST',
        data,
      }),
      invalidatesTags: result => [
        'AdmissionClass',
        { type: 'AdmissionClass', id: result?.id },
      ],
    }),
    updateAdmissionClass: builder.mutation<
      AdmissionClass,
      AdmissionClassRequestBody
    >({
      query: data => ({
        url: `/${data.id}`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: result => [
        'AdmissionClass',
        { type: 'AdmissionClass', id: result?.id },
      ],
    }),
    deleteAdmissionClassById: builder.mutation<void, string>({
      query: id => ({
        url: `/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_, __, id) => [
        'AdmissionClass',
        { type: 'AdmissionClass', id },
      ],
    }),
  }),
});

export const { useGetAdmissionClassesQuery } = admissionClassesApi;

export const useGetAdmissionClassesIInfiniteQuery = normalizeInfiniteQueryData(
  admissionClassesApi.useGetAdmissionClassesIInfiniteQuery,
);
